import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Github, RefreshCw, CheckCircle, ExternalLink, GitBranch, GitCommit, AlertCircle, Loader2, ListTodo } from 'lucide-react';
import { toast } from '../hooks/use-toast';


const GitHubIntegration = ({ workspaceId }) => {
  const [integration, setIntegration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [selectedRepos, setSelectedRepos] = useState([]);
  const [activity, setActivity] = useState([]);
  const [showRepoSelector, setShowRepoSelector] = useState(false);
  
  // Convert to task states
  const [showConvertDialog, setShowConvertDialog] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [boards, setBoards] = useState([]);
  const [selectedBoard, setSelectedBoard] = useState('');

  useEffect(() => {
    fetchIntegration();
    fetchActivity();
    fetchBoards();
  }, [workspaceId]);
  
  const fetchBoards = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/boards?workspace_id=${workspaceId}`,
        { withCredentials: true }
      );
      setBoards(response.data);
    } catch (error) {
      console.error('Error fetching boards:', error);
    }
  };

  const fetchIntegration = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/github/integration?workspace_id=${workspaceId}`,
        { withCredentials: true }
      );
      setIntegration(response.data);
      
      if (response.data.connected && response.data.repos) {
        const enabled = response.data.repos
          .filter(r => r.enabled)
          .map(r => r.id);
        setSelectedRepos(enabled);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching GitHub integration:', error);
      setLoading(false);
    }
  };

  const fetchActivity = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/github/activity?workspace_id=${workspaceId}&limit=20`,
        { withCredentials: true }
      );
      setActivity(response.data);
    } catch (error) {
      console.error('Error fetching activity:', error);
    }
  };

  const handleConnectGitHub = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/github/oauth-url?workspace_id=${workspaceId}`,
        { withCredentials: true }
      );
      
      // Open OAuth in popup
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      
      const popup = window.open(
        response.data.oauth_url,
        'GitHub OAuth',
        `width=${width},height=${height},left=${left},top=${top}`
      );
      
      // Listen for OAuth callback
      const handleMessage = async (event) => {
        // Verify origin for security
        if (event.origin !== window.location.origin) return;
        
        if (event.data.type === 'github-oauth-success') {
          // Remove listener after receiving message
          window.removeEventListener('message', handleMessage);
          
          if (popup && !popup.closed) {
            popup.close();
          }
          
          try {
            // Complete connection
            const connectResponse = await axios.post(
              `${API_BASE_URL}/api/github/connect`,
              {
                code: event.data.code,
                workspace_id: workspaceId
              },
              { withCredentials: true }
            );
            
            toast({ 
              title: '✅ GitHub connected successfully!',
              description: `Connected as ${connectResponse.data.github_user}`
            });
            fetchIntegration();
            fetchActivity();
            
            // Auto-open repo selector for first-time setup
            setShowRepoSelector(true);
            
            // Auto-sync after a short delay
            setTimeout(() => {
              handleSyncNow();
            }, 1000);
          } catch (error) {
            console.error('GitHub connection error:', error);
            const errorMsg = error.response?.data?.detail || 'Failed to connect GitHub';
            toast({ title: 'Connection failed', description: errorMsg, variant: 'destructive' });
          }
        }
      };
      
      window.addEventListener('message', handleMessage);
      
      // Cleanup listener if popup is closed without completing OAuth
      const checkPopup = setInterval(() => {
        if (popup && popup.closed) {
          clearInterval(checkPopup);
          window.removeEventListener('message', handleMessage);
        }
      }, 500);
      
    } catch (error) {
      console.error('Error initiating GitHub OAuth:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to initiate GitHub connection';
      toast({ title: 'Connection failed', description: errorMsg, variant: 'destructive' });
    }
  };

  const handleSyncNow = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/github/sync?workspace_id=${workspaceId}`,
        {},
        { withCredentials: true }
      );
      
      toast({ 
        title: '✅ Sync complete!', 
        description: `${response.data.activities_added} new activities synced`
      });
      
      fetchActivity();
    } catch (error) {
      toast({ title: 'Sync failed', variant: 'destructive' });
    } finally {
      setSyncing(false);
    }
  };

  const handleSaveRepos = async () => {
    try {
      await axios.patch(
        `${API_BASE_URL}/api/github/repos?workspace_id=${workspaceId}`,
        { repo_ids: selectedRepos },
        { withCredentials: true }
      );
      
      toast({ title: '✅ Repository selection saved!' });
      setShowRepoSelector(false);
      handleSyncNow(); // Auto-sync after saving
    } catch (error) {
      toast({ title: 'Failed to save repositories', variant: 'destructive' });
    }
  };

  const handleConvertToTask = async () => {
    if (!selectedBoard || !selectedIssue) {
      toast({ title: 'Please select a board', variant: 'destructive' });
      return;
    }
    
    try {
      // Extract issue number from URL
      const issueNumber = selectedIssue.url.match(/\/issues\/(\d+)/)?.[1];
      
      await axios.post(
        `${API_BASE_URL}/api/github/issue-to-task?workspace_id=${workspaceId}`,
        {
          boardId: selectedBoard,
          issue_url: selectedIssue.url,
          issue_number: parseInt(issueNumber),
          repo_name: selectedIssue.repo_name,
          title: selectedIssue.title,
          description: selectedIssue.description,
          assignees: [],
          labels: []
        },
        { withCredentials: true }
      );
      
      toast({ 
        title: '✅ Task created from GitHub issue!',
        description: `"${selectedIssue.title}" has been added to your board`
      });
      
      setShowConvertDialog(false);
      setSelectedIssue(null);
      setSelectedBoard('');
      
      // Refresh activity to show linked status
      fetchActivity();
    } catch (error) {
      console.error('Error converting issue:', error);
      toast({ 
        title: 'Failed to create task',
        description: error.response?.data?.detail || 'Please try again',
        variant: 'destructive'
      });
    }
  };
  


  const toggleRepo = (repoId) => {
    setSelectedRepos(prev =>
      prev.includes(repoId)
        ? prev.filter(id => id !== repoId)
        : [...prev, repoId]
    );
  };

  const getActivityIcon = (type) => {
    switch(type) {
      case 'commit': return <GitCommit className="h-4 w-4 text-blue-500" />;
      case 'issue': return <AlertCircle className="h-4 w-4 text-yellow-500" />;
      case 'pr': return <GitBranch className="h-4 w-4 text-purple-500" />;
      default: return <Github className="h-4 w-4 text-gray-500" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!integration?.connected) {
    return (
      <div className="max-w-2xl mx-auto text-center py-12">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <Github className="h-8 w-8 text-gray-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-3">Connect GitHub</h2>
        <p className="text-gray-600 mb-8">
          Sync your repositories, track commits, issues, and PRs directly in Doozum. 
          Convert GitHub issues into tasks with one click.
        </p>
        <Button 
          onClick={handleConnectGitHub}
          size="lg"
          className="bg-gray-900 hover:bg-gray-800"
        >
          <Github className="h-5 w-5 mr-2" />
          Connect with GitHub
        </Button>
        <p className="text-xs text-gray-500 mt-4">
          Free • No credit card required • OAuth2 secure authentication
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
            <CheckCircle className="h-5 w-5 text-green-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">GitHub Connected</h3>
            <p className="text-sm text-gray-600">
              @{integration.github_user} • {integration.repos?.length || 0} repositories
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setShowRepoSelector(!showRepoSelector)}
            variant="outline"
            size="sm"
          >
            <Github className="h-4 w-4 mr-2" />
            {showRepoSelector ? 'Close' : 'Manage Repos'}
          </Button>
          <Button
            onClick={handleSyncNow}
            disabled={syncing}
            variant="outline"
            size="sm"
          >
            {syncing ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Sync Now
          </Button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-1">
            <Github className="h-4 w-4 text-blue-600" />
            <span className="text-xs font-medium text-blue-900">Repositories</span>
          </div>
          <p className="text-2xl font-bold text-blue-600">
            {integration.repos?.length || 0}
          </p>
          <p className="text-xs text-blue-600 mt-1">
            {selectedRepos.length} tracked
          </p>
        </div>
        
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-1">
            <GitBranch className="h-4 w-4 text-purple-600" />
            <span className="text-xs font-medium text-purple-900">Pull Requests</span>
          </div>
          <p className="text-2xl font-bold text-purple-600">
            {activity.filter(a => a.activity_type === 'pr').length}
          </p>
        </div>
        
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-1">
            <AlertCircle className="h-4 w-4 text-yellow-600" />
            <span className="text-xs font-medium text-yellow-900">Open Issues</span>
          </div>
          <p className="text-2xl font-bold text-yellow-600">
            {activity.filter(a => a.activity_type === 'issue' && a.status === 'open').length}
          </p>
        </div>
      </div>

      {/* Repository Selector */}
      {showRepoSelector && (
        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
          <h4 className="font-semibold mb-3">Select Repositories to Track</h4>
          <p className="text-sm text-gray-600 mb-3">
            Choose which repositories you want to sync with Doozum
          </p>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {integration.repos?.map(repo => (
              <div
                key={repo.id}
                className="flex items-center gap-3 p-3 bg-white rounded border hover:border-orange-300 transition-colors"
              >
                <Checkbox
                  checked={selectedRepos.includes(repo.id)}
                  onCheckedChange={() => toggleRepo(repo.id)}
                />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm text-gray-900">{repo.name}</p>
                  {repo.description && (
                    <p className="text-xs text-gray-500 truncate">{repo.description}</p>
                  )}
                  <a 
                    href={repo.html_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-orange-600 hover:underline inline-flex items-center gap-1 mt-1"
                  >
                    View on GitHub
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
                {repo.enabled && (
                  <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700">
                    Tracking
                  </span>
                )}
              </div>
            ))}
          </div>
          <div className="flex gap-2 mt-4">
            <Button onClick={handleSaveRepos} size="sm" disabled={selectedRepos.length === 0}>
              Save Selection ({selectedRepos.length})
            </Button>
            <Button onClick={() => setShowRepoSelector(false)} variant="outline" size="sm">
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Tracked Repositories Summary (always visible) */}
      {!showRepoSelector && selectedRepos.length > 0 && (
        <div className="border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold">Tracked Repositories ({selectedRepos.length})</h4>
            <Button 
              onClick={() => setShowRepoSelector(true)}
              variant="ghost"
              size="sm"
              className="text-orange-600 hover:text-orange-700"
            >
              Edit
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {integration.repos
              ?.filter(r => selectedRepos.includes(r.id))
              .map(repo => (
                <a
                  key={repo.id}
                  href={repo.html_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm transition-colors"
                >
                  <Github className="h-4 w-4 text-gray-600" />
                  <span className="font-medium text-gray-900">{repo.name}</span>
                  <ExternalLink className="h-3 w-3 text-gray-400" />
                </a>
              ))}
          </div>
        </div>
      )}

      {/* Activity Feed */}
      <div>
        <h4 className="font-semibold mb-3">Recent Activity</h4>
        {activity.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Github className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p className="text-sm">No activity yet. Click "Sync Now" to fetch data.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {activity.map((item, index) => (
              <div
                key={index}
                className="flex items-start gap-3 p-3 bg-white border border-gray-200 rounded-lg hover:border-orange-300 transition-colors"
              >
                {getActivityIcon(item.activity_type)}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">{item.title}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-gray-500">{item.author}</span>
                    <span className="text-xs text-gray-400">•</span>
                    <span className="text-xs text-gray-500">{item.repo_name}</span>
                    {item.status && (
                      <>
                        <span className="text-xs text-gray-400">•</span>
                        <span className={`text-xs px-1.5 py-0.5 rounded ${
                          item.status === 'open' ? 'bg-green-100 text-green-700' :
                          item.status === 'closed' ? 'bg-gray-100 text-gray-700' :
                          'bg-purple-100 text-purple-700'
                        }`}>
                          {item.status}
                        </span>
                      </>
                    )}
                    {item.linked_task_id && (
                      <>
                        <span className="text-xs text-gray-400">•</span>
                        <span className="text-xs px-1.5 py-0.5 rounded bg-orange-100 text-orange-700">
                          ✓ Linked to task
                        </span>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {/* Convert to Task button for issues */}
                  {item.activity_type === 'issue' && !item.linked_task_id && (
                    <Button
                      onClick={() => {
                        setSelectedIssue(item);
                        setShowConvertDialog(true);
                      }}
                      size="sm"
                      variant="outline"
                      className="text-xs"
                    >
                      <ListTodo className="h-3 w-3 mr-1" />
                      Create Task
                    </Button>
                  )}
                  <a
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-orange-600"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Convert Issue to Task Dialog */}
      <Dialog open={showConvertDialog} onOpenChange={setShowConvertDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ListTodo className="h-5 w-5 text-orange-500" />
              Convert GitHub Issue to Task
            </DialogTitle>
            <DialogDescription>
              Create a Doozum task from this GitHub issue
            </DialogDescription>
          </DialogHeader>
          
          {selectedIssue && (
            <div className="space-y-4 py-4">
              {/* Issue Preview */}
              <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-start gap-2 mb-2">
                  <AlertCircle className="h-4 w-4 text-yellow-500 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">{selectedIssue.title}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {selectedIssue.repo_name} • {selectedIssue.author}
                    </p>
                  </div>
                </div>
                {selectedIssue.description && (
                  <p className="text-xs text-gray-600 line-clamp-3 mt-2">
                    {selectedIssue.description}
                  </p>
                )}
              </div>
              
              {/* Board Selection */}
              <div>
                <Label htmlFor="convert-board">Select Board *</Label>
                <Select value={selectedBoard} onValueChange={setSelectedBoard}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Choose a board" />
                  </SelectTrigger>
                  <SelectContent>
                    {boards.map(board => (
                      <SelectItem key={board._id} value={board._id}>
                        {board.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="text-xs text-gray-500 p-3 bg-blue-50 border border-blue-200 rounded">
                💡 The task will include a link back to the GitHub issue and will be marked as imported from GitHub.
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowConvertDialog(false);
                setSelectedIssue(null);
                setSelectedBoard('');
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleConvertToTask}
              disabled={!selectedBoard}
              className="bg-orange-500 hover:bg-orange-600"
            >
              <ListTodo className="h-4 w-4 mr-2" />
              Create Task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GitHubIntegration;
